import dayjs from 'dayjs';

import { DATE_SIMPLE, HOURS_IN_DAY } from '@cast/constants';
import { PriceType } from '@cast/shared-types';
import { getPriceMultiplier } from '@cast/utils';

import { CostReportDataByPeriod } from 'features/cost-report/types/costOverTime';
import { convertChartDatapointToCostReportData } from 'features/cost-report/utils/costOverTime';
import { getDailyAverageMetrics } from 'features/cost-report/utils/metrics/getDailyAverageMetrics';

import { CostReportTableData } from '../types';

export const mapToSelectedRate = (
  datapoint: CostReportDataByPeriod,
  params: {
    timezone: string;
  }
): CostReportTableData => {
  const { timezone } = params;

  const multiplier = getPriceMultiplier(PriceType.DAILY);

  const totalUptimeInMinutes = Math.max(
    datapoint.onDemandUptime,
    datapoint.fallbackUptime,
    datapoint.spotUptime
  );

  const onDemand =
    ((datapoint.onDemandCost * datapoint.onDemandUptime) / 60) * multiplier;
  const fallback =
    ((datapoint.fallbackCost * datapoint.fallbackUptime) / 60) * multiplier;
  const spot = ((datapoint.spotCost * datapoint.spotUptime) / 60) * multiplier;
  const storage = datapoint.totalStorageCost * multiplier;
  const total = onDemand + spot + fallback + storage;

  return {
    date: dayjs.utc(datapoint.timestamp).tz(timezone).format(DATE_SIMPLE),
    countCpu: {
      total: datapoint.totalCpu,
      onDemand: datapoint.onDemandCpuCount,
      fallback: datapoint.fallbackCpuCount,
      spot: datapoint.spotCpuCount,
    },
    countGpu: {
      total: datapoint.totalGpu,
      onDemand: datapoint.onDemandGpuCount,
      fallback: datapoint.fallbackGpuCount,
      spot: datapoint.spotGpuCount,
    },
    countTpu: {
      total: datapoint.totalTpu,
      onDemand: datapoint.onDemandTpuCount,
      fallback: datapoint.fallbackTpuCount,
      spot: datapoint.spotTpuCount,
    },
    countRamGib: {
      total: datapoint.totalRamGib,
      onDemand: datapoint.onDemandRamGib,
      fallback: datapoint.fallbackRamGib,
      spot: datapoint.spotRamGib,
    },
    countStorageGib: {
      total: datapoint.totalStorage,
      onDemand: datapoint.totalStorage,
      fallback: 0,
      spot: 0,
    },
    totalUptimeCpuCost:
      ((datapoint.totalCpuCost * totalUptimeInMinutes) / 60) * multiplier,
    totalUptimeGpuCost:
      ((datapoint.totalGpuCost * totalUptimeInMinutes) / 60) * multiplier,
    totalUptimeTpuCost:
      ((datapoint.totalTpuCost * totalUptimeInMinutes) / 60) * multiplier,
    totalUptimeRamCost:
      ((datapoint.totalRamCost * totalUptimeInMinutes) / 60) * multiplier,
    totalUptimeStorageCost:
      ((datapoint.totalStorageCost * totalUptimeInMinutes) / 60) * multiplier,
    totalUptimeCost: {
      total,
      onDemand,
      fallback,
      spot,
      storage,
    },
  };
};

export const mapToUptimeCost = (
  datapoint: CostReportDataByPeriod,
  params: {
    timezone: string;
    storageCostGranularity: PriceType;
  }
): CostReportTableData => {
  const { timezone, storageCostGranularity } = params;

  const totalUptimeInMinutes = Math.max(
    datapoint.onDemandUptime,
    datapoint.fallbackUptime,
    datapoint.spotUptime
  );

  let onDemandPodCount = 0;
  if ('onDemandPodCount' in datapoint) {
    onDemandPodCount = datapoint.onDemandPodCount;
  }

  let fallbackPodCount = 0;
  if ('fallbackPodCount' in datapoint) {
    fallbackPodCount = datapoint.fallbackPodCount;
  }

  let spotPodCount = 0;
  if ('spotPodCount' in datapoint) {
    spotPodCount = datapoint.spotPodCount;
  }

  const totalPodCount = onDemandPodCount + fallbackPodCount + spotPodCount;

  const onDemandCost = (datapoint.onDemandCost * datapoint.onDemandUptime) / 60;
  const fallbackCost = (datapoint.fallbackCost * datapoint.fallbackUptime) / 60;
  const spotCost = (datapoint.spotCost * datapoint.spotUptime) / 60;
  const storageCost =
    storageCostGranularity === PriceType.HOURLY
      ? datapoint.totalStorageCost * HOURS_IN_DAY
      : datapoint.totalStorageCost;

  const totalUptimeCost = {
    onDemand: onDemandCost,
    fallback: fallbackCost,
    spot: spotCost,
    storage: storageCost,
    total: onDemandCost + spotCost + fallbackCost + storageCost,
  };

  return {
    date: dayjs.utc(datapoint.timestamp).tz(timezone).format(DATE_SIMPLE),
    countCpu: {
      total: datapoint.totalCpu,
      onDemand: datapoint.onDemandCpuCount,
      fallback: datapoint.fallbackCpuCount,
      spot: datapoint.spotCpuCount,
    },
    countGpu: {
      total: datapoint.totalGpu,
      onDemand: datapoint.onDemandGpuCount,
      fallback: datapoint.fallbackGpuCount,
      spot: datapoint.spotGpuCount,
    },
    countTpu: {
      total: datapoint.totalTpu,
      onDemand: datapoint.onDemandTpuCount,
      fallback: datapoint.fallbackTpuCount,
      spot: datapoint.spotTpuCount,
    },
    countRamGib: {
      total: datapoint.totalRamGib,
      onDemand: datapoint.onDemandRamGib,
      fallback: datapoint.fallbackRamGib,
      spot: datapoint.spotRamGib,
    },
    countStorageGib: {
      total: datapoint.totalStorage,
      onDemand: datapoint.totalStorage,
      fallback: 0,
      spot: 0,
    },
    totalUptimeCpuCost: (datapoint.totalCpuCost * totalUptimeInMinutes) / 60,
    totalUptimeGpuCost: (datapoint.totalGpuCost * totalUptimeInMinutes) / 60,
    totalUptimeTpuCost: (datapoint.totalTpuCost * totalUptimeInMinutes) / 60,
    totalUptimeRamCost: (datapoint.totalRamCost * totalUptimeInMinutes) / 60,
    totalUptimeStorageCost: storageCost,

    normalizedCostPerPod:
      totalPodCount > 0 ? totalUptimeCost.total / totalPodCount : 0,

    podCount: {
      total: totalPodCount,
      onDemand: onDemandPodCount,
      fallback: fallbackPodCount,
      spot: spotPodCount,
    },

    totalUptimeCost,
  };
};

type MapCostReportChartDataToTableDataParams = {
  chartData: CostReportDataByPeriod[];
  timezone: string;
  storageCostGranularity: PriceType;
  projectEndOfDayCost: boolean;
};

export const mapCostReportChartDataToTableData = ({
  chartData,
  timezone,
  storageCostGranularity,
  projectEndOfDayCost,
}: MapCostReportChartDataToTableDataParams): CostReportTableData[] => {
  if (!chartData.length) {
    return [];
  }

  const passedHourlyData = convertChartDatapointToCostReportData(
    chartData
  ).filter((item) => !item.forecast);

  const dailyAverageData = getDailyAverageMetrics(
    passedHourlyData,
    {
      includeGenerated: false,
      includeForecasted: true,
    },
    timezone
  );

  const params = {
    timezone,
    storageCostGranularity,
  };

  return dailyAverageData
    .map((datapoint) =>
      projectEndOfDayCost
        ? mapToSelectedRate(datapoint, params)
        : mapToUptimeCost(datapoint, params)
    )
    .reverse();
};
